import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { FileText, Check, ArrowLeft, HelpCircle, ChevronDown, Sparkles, Building2, Zap, Loader2, Users, Crown, MessageSquare, Shield, Heart, Car, Home, Briefcase } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { supabase } from "@/integrations/supabase/client";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import { SEO } from "@/components/seo/SEO";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";
import { IOSUpgradeModal, isIOSDevice } from "@/components/pricing/IOSUpgradeModal";
import { PLANS, type PlanId, formatLimit, isUnlimited } from "@/config/plans";

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [audience, setAudience] = useState<'b2c' | 'b2b'>('b2c');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [selectedPlanForIOS, setSelectedPlanForIOS] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user);
    });
  }, []);

  const formatPrice = (euros: number) => {
    if (euros === 0) return t.common.free;
    return `${euros.toFixed(2).replace('.', ',')}€`;
  };

  const formatPriceShort = (euros: number) => {
    if (euros === 0) return "0";
    return euros % 1 === 0 ? euros.toString() : euros.toFixed(2).replace('.', ',');
  };

  const getPlanIcon = (slug: PlanId) => {
    switch (slug) {
      case 'discovery': return <Zap className="w-5 h-5" />;
      case 'solo': return <FileText className="w-5 h-5" />;
      case 'famille': return <Heart className="w-5 h-5" />;
      case 'pro': return <Sparkles className="w-5 h-5" />;
      case 'team': return <Users className="w-5 h-5" />;
      case 'business': return <Building2 className="w-5 h-5" />;
      case 'enterprise': return <Crown className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getPlanColor = (slug: PlanId) => {
    switch (slug) {
      case 'discovery': return 'from-emerald-500 to-teal-500';
      case 'solo': return 'from-blue-500 to-cyan-500';
      case 'famille': return 'from-pink-500 to-rose-500';
      case 'pro': return 'from-primary to-purple-500';
      case 'team': return 'from-orange-500 to-amber-500';
      case 'business': return 'from-slate-600 to-slate-800';
      case 'enterprise': return 'from-violet-600 to-indigo-600';
      default: return 'from-primary to-primary/60';
    }
  };

  const handleCheckout = async (planId: PlanId) => {
    if (!user) {
      navigate("/auth?mode=signup");
      return;
    }

    if (planId === 'discovery') {
      toast.error(t.pricing.toasts.freeNoPayment);
      return;
    }

    if (planId === 'enterprise') {
      window.location.href = "mailto:contact@easyfinder.fr?subject=Demande%20Enterprise";
      return;
    }

    // iOS App Store compliant flow
    if (isIOSDevice()) {
      setSelectedPlanForIOS({ id: planId, name: PLANS[planId].name });
      setIosModalOpen(true);
      return;
    }

    setCheckingOut(planId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          planSlug: planId,
          billingCycle: isYearly ? 'yearly' : 'monthly',
          successUrl: `${window.location.origin}/documents?success=true`,
          cancelUrl: `${window.location.origin}/pricing?canceled=true`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(t.pricing.toasts.paymentUrlError);
      }
    } catch (error: any) {
      toast.error(error.message || t.pricing.toasts.paymentError);
    } finally {
      setCheckingOut(null);
    }
  };

  const handleContactSales = () => {
    window.location.href = "mailto:contact@easyfinder.fr?subject=Demande%20Enterprise%20EasyFinder";
  };

  // Plans séparés par audience
  const b2cPlans: PlanId[] = ['solo', 'famille'];
  const b2bPlans: PlanId[] = ['pro', 'team', 'business'];
  const discoveryPlan = PLANS.discovery;
  const enterprisePlan = PLANS.enterprise;
  
  // Helper pour afficher l'indicateur B2B
  const getB2BLabel = (b2bAccess: string) => {
    switch (b2bAccess) {
      case 'single': return '1 module B2B';
      case 'all': return 'Tous modules B2B';
      default: return null;
    }
  };

  // Plans à afficher selon l'onglet actif
  const currentPlans = audience === 'b2c' ? b2cPlans : b2bPlans;

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Tarifs"
        description="Découvrez nos offres EasyFinder : Gratuit, Solo, Pro, Team, Business et Enterprise. Choisissez le plan adapté à vos besoins de gestion documentaire."
        canonical="/pricing"
      />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img src={easyfinderLogo} alt="EasyFinder" className="w-8 h-8 rounded-lg shadow-lg shadow-primary/25" />
              <span className="text-xl font-bold">EasyFinder</span>
            </Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="hidden sm:flex">{t.common.signIn}</Button>
            </Link>
            <Link to="/auth?mode=signup">
              <Button variant="hero" size="sm">{t.common.getStarted}</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pt-32 pb-20">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 px-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-medium mb-4"
          >
            <Sparkles className="w-4 h-4 shrink-0" />
            <span className="text-center">{t.pricing.launchOffer}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
          >
            {t.pricing.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base sm:text-lg text-muted-foreground"
          >
            {t.pricing.subtitle}
          </motion.p>
        </div>

        {/* Audience Tabs + Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col items-center gap-6 mb-10"
        >
          {/* Audience Tabs */}
          <Tabs value={audience} onValueChange={(v) => setAudience(v as 'b2c' | 'b2b')} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2 h-12">
              <TabsTrigger value="b2c" className="flex items-center gap-2 text-sm">
                <Home className="w-4 h-4" />
                Particuliers
              </TabsTrigger>
              <TabsTrigger value="b2b" className="flex items-center gap-2 text-sm">
                <Briefcase className="w-4 h-4" />
                Professionnels
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Billing Toggle */}
          <div className="flex items-center gap-4">
            <span className={`text-sm font-medium ${!isYearly ? 'text-foreground' : 'text-muted-foreground'}`}>
              {t.pricing.monthly}
            </span>
            <Switch checked={isYearly} onCheckedChange={setIsYearly} />
            <span className={`text-sm font-medium ${isYearly ? 'text-foreground' : 'text-muted-foreground'}`}>
              {t.pricing.yearly}
            </span>
            {isYearly && (
              <Badge variant="success" className="ml-2">
                {t.pricing.monthsFree}
              </Badge>
            )}
          </div>
        </motion.div>

        {/* Audience-specific subtitle */}
        <motion.p
          key={audience}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-sm text-muted-foreground mb-8 max-w-lg mx-auto"
        >
          {audience === 'b2c' 
            ? "Gérez vos documents personnels et ceux de votre famille en toute simplicité."
            : "Outils métier inclus : Flotte, Immobilier, Appels d'offres, Prestataires."}
        </motion.p>

        {/* Plans Grid - adapté selon l'audience */}
        <div className={`grid grid-cols-1 ${currentPlans.length === 2 ? 'md:grid-cols-2 max-w-3xl' : 'md:grid-cols-3 max-w-5xl'} gap-6 mx-auto mb-8 px-2 sm:px-0`}>
          {currentPlans.map((planId, i) => {
            const plan = PLANS[planId];
            const isPopular = audience === 'b2b' && planId === 'pro';
            const hasB2B = plan.b2bAccess !== 'none';
            const b2bLabel = getB2BLabel(plan.b2bAccess);
            
            return (
              <motion.div
                key={planId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i + 0.3 }}
                className="relative"
              >
                <Card
                  className={`relative h-full transition-all duration-300 hover:shadow-xl ${
                    isPopular
                      ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]'
                      : 'border-border bg-card shadow-sm hover:border-primary/30 hover:shadow-md'
                  }`}
                  variant="elevated"
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-gradient-to-r from-primary to-purple-500 text-white px-4 py-1">
                        {t.pricing.mostPopular}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="pb-4 pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getPlanColor(planId)} flex items-center justify-center text-white`}>
                        {getPlanIcon(planId)}
                      </div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                    </div>
                    <CardDescription className="text-sm">{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      {isYearly ? (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold">
                              {formatPriceShort(plan.priceYearly)}
                            </span>
                            <span className="text-xl font-bold">€</span>
                            <span className="text-muted-foreground">{t.common.perYear}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            soit {(plan.priceYearly / 12).toFixed(2).replace('.', ',')}€/mois
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold">
                              {formatPriceShort(plan.priceMonthly)}
                            </span>
                            <span className="text-xl font-bold">€</span>
                            <span className="text-muted-foreground">{t.common.perMonth}</span>
                          </div>
                          <p className="text-sm text-success mt-1">
                            -{formatPrice(plan.priceMonthly * 12 - plan.priceYearly)} en annuel
                          </p>
                        </>
                      )}
                    </div>

                    {/* Key limits */}
                    <div className="space-y-2 py-4 border-y border-border text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t.pricing.documents}</span>
                        <span className="font-medium">{formatLimit(plan.limits.storage)}</span>
                      </div>
                      {plan.limits.familyMembers > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Membres famille</span>
                          <span className="font-medium">{plan.limits.familyMembers} inclus</span>
                        </div>
                      )}
                      {plan.limits.users > 1 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Utilisateurs</span>
                          <span className="font-medium">{formatLimit(plan.limits.users)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t.pricing.aiSearchesDay}</span>
                        <span className="font-medium">{formatLimit(plan.limits.aiDaily)}</span>
                      </div>
                      {hasB2B && (
                        <TooltipProvider>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-between pt-2 mt-2 border-t border-dashed border-border cursor-help">
                                <span className="text-muted-foreground flex items-center gap-1.5">
                                  <Briefcase className="w-4 h-4" />
                                  Outils métier
                                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
                                </span>
                                <Badge variant="outline" className="text-xs px-2 py-0.5 bg-primary/5 border-primary/20 text-primary">
                                  {plan.b2bAccess === 'single' ? '1 au choix' : 'Tous inclus'}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs p-3 bg-popover border border-border">
                              <p className="font-medium text-sm mb-2">Outils métier inclus :</p>
                              <ul className="text-xs space-y-1.5 text-muted-foreground">
                                <li className="flex items-center gap-2">
                                  <Car className="w-3.5 h-3.5 text-blue-500" />
                                  <span><strong>Flotte</strong> — véhicules, assurances, CT</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Building2 className="w-3.5 h-3.5 text-orange-500" />
                                  <span><strong>Immobilier</strong> — baux, diagnostics, quittances</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <FileText className="w-3.5 h-3.5 text-purple-500" />
                                  <span><strong>Appels d'offres</strong> — analyse DCE, checklist</span>
                                </li>
                                <li className="flex items-center gap-2">
                                  <Users className="w-3.5 h-3.5 text-green-500" />
                                  <span><strong>Prestataires</strong> — KBIS, attestations, portail</span>
                                </li>
                              </ul>
                              {plan.b2bAccess === 'single' && (
                                <p className="text-[10px] mt-2 pt-2 border-t border-dashed text-muted-foreground">
                                  Plan Pro : choisissez 1 module à l'inscription
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>

                    {/* Features list */}
                    <div className="space-y-2 text-sm">
                      {plan.features.slice(0, 5).map((feature, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-success shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {plan.features.length > 5 && (
                        <p className="text-muted-foreground text-xs pl-6">
                          + {plan.features.length - 5} autres fonctionnalités
                        </p>
                      )}
                    </div>

                    <div className="block pt-2">
                      <Button
                        className={`w-full ${isPopular ? 'bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90' : ''}`}
                        variant={isPopular ? 'default' : 'outline'}
                        size="lg"
                        onClick={() => handleCheckout(planId)}
                        disabled={checkingOut === planId}
                      >
                        {checkingOut === planId ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t.pricing.redirecting}
                          </>
                        ) : (
                          t.pricing.choosePlan
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Discovery Plan - Pour B2C uniquement */}
        {audience === 'b2c' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-2xl mx-auto mb-8 px-2 sm:px-0"
          >
            <div className="relative p-5 rounded-xl bg-muted/30 border border-border/50 hover:border-border transition-colors">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{discoveryPlan.name}</h3>
                      <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                        {t.common.free}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Testez EasyFinder sans engagement
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {discoveryPlan.limits.storage} documents
                      </span>
                      <span className="flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" />
                        {discoveryPlan.limits.aiDaily} recherches IA/jour
                      </span>
                    </div>
                  </div>
                </div>
                <Link to="/auth?mode=signup" className="shrink-0 w-full sm:w-auto">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto">
                    {t.pricing.startFree}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Enterprise Plan - Pour B2B uniquement */}
        {audience === 'b2b' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="max-w-4xl mx-auto mb-8 px-2 sm:px-0"
          >
            <Card className="bg-gradient-to-br from-violet-500/5 via-background to-indigo-500/5 border-violet-500/30 hover:border-violet-500/50 transition-colors">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shrink-0 shadow-lg shadow-violet-500/25">
                      <Crown className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-foreground">{enterprisePlan.name}</h3>
                        <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                          Sur devis
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        {enterprisePlan.description}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        {enterprisePlan.features.slice(0, 6).map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-muted-foreground">
                            <Check className="w-4 h-4 text-violet-500 shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 shrink-0 w-full lg:w-auto">
                    <Button 
                      size="lg" 
                      className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-lg shadow-violet-500/25"
                      onClick={handleContactSales}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Nous contacter
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      À partir de 399€/mois
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Comparison note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-muted-foreground text-sm">
            {t.pricing.allPlansInclude}
          </p>
        </motion.div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">
            {t.pricing.faq.title}
          </h2>
          <div className="space-y-4">
            {t.pricing.faq.items.map((item, i) => (
              <Collapsible key={i}>
                <Card>
                  <CollapsibleTrigger className="w-full">
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3 text-left">
                        <HelpCircle className="w-5 h-5 text-primary shrink-0" />
                        <span className="font-medium">{item.q}</span>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </CardContent>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4 pl-12">
                      <p className="text-muted-foreground">{item.a}</p>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <FileText className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-semibold">EasyFinder</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Confidentialité</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">CGU</Link>
          </nav>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} EasyFinder. {t.pricing.footer}
          </p>
        </div>
      </footer>

      {/* iOS Upgrade Modal */}
      {selectedPlanForIOS && user && (
        <IOSUpgradeModal
          open={iosModalOpen}
          onOpenChange={setIosModalOpen}
          planName={selectedPlanForIOS.name}
          planId={selectedPlanForIOS.id}
          billingCycle={isYearly ? "yearly" : "monthly"}
          userEmail={user.email || ""}
        />
      )}
    </div>
  );
};

export default Pricing;
